import React from 'react';
import PropTypes from 'prop-types';
import { Deserializer } from 'jsonapi-serializer';
import Helmet from 'react-helmet';
import { withRouter } from 'react-router-dom';
import Alert from 'react-s-alert';

import fullname from 'utils/fullname';

import TaskMaterials from 'api/resources/taskMaterials';
import getErrors, { processErrorByAlert } from 'utils/getErrors';

import MaterialNotFound from 'components/MaterialNotFound';
import MaterialHeaderStub from 'admin-moderator/components/MaterialHeaderStub';
import MaterialUserStub from 'admin-moderator/components/MaterialUserStub';
import MaterialHeader from 'admin-moderator/components/MaterialHeader';
import MaterialUser from 'admin-moderator/components/MaterialUser';
import MaterialCard from 'admin-moderator/containers/MaterialCard';
import MaterialStory from 'admin-moderator/containers/MaterialStory';

import CenteredContainer from 'styled/Page/CenteredContainer';
import ErrorText from 'styled/Page/ErrorText';

const STATUSES = {
  accepted: 'одобрен',
  rejected: 'отклонен',
};

export class MaterialPage extends React.PureComponent {
  state = {
    loading: false,
    formLoading: false,
    material: {},
    errors: [],
  }

  componentDidMount() {
    this.loadMaterial();
  }

  componentDidUpdate(prevProps) {
    if (this.props.match.params.materialId !== prevProps.match.params.materialId) {
      this.loadMaterial();
    }
  }

  get title() {
    return this.state.material.title || this.props.match.params.materialId;
  }

  deserialize = async response => (
    new Deserializer({
      keyForAttribute: 'camelCase',
      video: {
        valueForRelationship: (relationship, included) => ({
          ...included,
          type: relationship.type,
        }),
      },
      image: {
        valueForRelationship: (relationship, included) => ({
          ...included,
          type: relationship.type,
        }),
      },
    }).deserialize(response)
  )

  loadMaterial = async () => {
    this.setState({ loading: true, errors: [] });

    try {
      const response = await TaskMaterials.get({
        materialId: this.props.match.params.materialId,
        include: 'file,file.cover,user,user.avatar,user.oauth_providers,user.current_ban',
        fields: {
          material: 'title,description,created_at,price,status,file',
          image: 'url,status',
          video: 'duration,url,original_url,status,cover',
          user: 'current_ban,first_name,last_name,oauth_providers,avatar',
          avatar: 'url',
          current_ban: 'comment,expired_at',
        },
      });

      this.setState({
        loading: false,
        material: await this.deserialize(response),
      });
    } catch (err) {
      this.setState({ loading: false });

      const errors = await getErrors(err);
      this.setState({ errors });
    }
  }

  handleStatusChange = (material) => {
    this.setState({ formLoading: true, errors: [] });
    this.materialUpdate(material);
  }

  handleUserBan = (user) => {
    this.setState(prevState => ({
      material: { ...prevState.material, user: { ...prevState.material.user, currentBan: user.currentBan } },
    }));
  }

  handleFormSubmit = (material) => {
    this.setState({ formLoading: true, errors: [] });
    this.materialUpdate(material);
  }

  materialUpdate = async (material) => {
    try {
      const response = await TaskMaterials.patch({
        materialId: this.props.match.params.materialId,
        body: {
          data: {
            type: 'task_material',
            attributes: { ...material },
          },
        },
        include: 'file,file.cover,user,user.avatar,user.oauth_providers,user.current_ban',
        fields: {
          material: 'title,description,created_at,price,status,status',
          image: 'url,status',
          video: 'duration,url,original_url,status,cover',
          user: 'current_ban,first_name,last_name,avatar,oauth_providers',
          avatar: 'url',
          current_ban: 'created_at',
        },
      });

      const newMaterial = await this.deserialize(response);

      this.setState({
        formLoading: false,
        material: newMaterial,
      });

      Alert.success(`Материал ${STATUSES[material.status] || 'изменен'}`);
      this.props.onUpdate(newMaterial);
    } catch (err) {
      processErrorByAlert(err);
      this.setState({ formLoading: false });
    }
  }

  render() {
    return (
      <React.Fragment>
        <Helmet>
          <title>{this.title}</title>
        </Helmet>

        {this.state.material.id && !this.state.loading && !this.state.formLoading && (
          <MaterialHeader
            id={this.state.material.id}
            title={this.state.material.title}
            mediaType={this.state.material.file && this.state.material.file.type}
            description={this.state.material.description}
            createdAt={this.state.material.createdAt * 1000}
            duration={this.state.material.file && this.state.material.file.duration}
            price={this.state.material.price}
            onFormSubmit={this.handleFormSubmit}
            status={this.state.material.status}
          />
        )}

        {(this.state.loading || this.state.formLoading) && (
          <MaterialHeaderStub />
        )}

        {this.state.material.user && !this.state.loading && (
          <MaterialUser
            userId={this.state.material.user.id}
            isBanned={Boolean(this.state.material.user.currentBan)}
            fullname={fullname(this.state.material.user)}
            avatar={this.state.material.user.avatar && this.state.material.user.avatar.url}
            socialLinks={this.state.material.user.oauthProviders}

            telegramText={this.state.material.title}

            onBan={this.handleUserBan}
            onSendToAuthor={this.props.onSendToAuthor}
          />
        )}

        {this.state.loading && (<MaterialUserStub />)}

        {(this.state.material.id || this.state.loading) && (
          <MaterialCard
            mediaType={this.state.material.file && this.state.material.file.type}
            title={this.state.material.title}
            url={this.state.material.file && this.state.material.file.url}
            cover={this.state.material.file && this.state.material.file.cover && this.state.material.file.cover.url}
            originalUrl={this.state.material.file && this.state.material.file.originalUrl}
            onStatusChange={this.handleStatusChange}
            status={this.state.material.status}
            loading={this.state.loading}
            videoStatus={this.state.material.file && this.state.material.file.status}
          />
        )}

        {this.state.material.id && !this.state.loading &&
          <MaterialStory id={this.props.match.params.materialId} user={this.state.user} />
        }

        {!this.state.material.id && !this.state.loading && (
          <MaterialNotFound text="Не удалось загрузить материал" />
        )}

        {this.state.errors.length > 0 && !this.state.loading && (
          <CenteredContainer>
            {this.state.errors.map(error => (
              <ErrorText role="alert" key={error}>{error}</ErrorText>
            ))}
          </CenteredContainer>
        )}
      </React.Fragment>
    );
  }
}

MaterialPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({ materialId: PropTypes.string }),
  }).isRequired,

  onUpdate: PropTypes.func,
  onSendToAuthor: PropTypes.func,
};

MaterialPage.defaultProps = {
  onUpdate: () => {},
  onSendToAuthor: () => {},
};

export default withRouter(MaterialPage);
